// The /albums/:id screen (ADR-0059): one Album's detail — a header (title + item count +
// inline rename + two-step delete + Back) over its curated media grid. Each item renders as
// the same <MediaCard> the gallery uses, with a per-item control row to set it as the Cover
// or Remove it from the album (the media itself is untouched). Items keep their added order
// (the reorder endpoint exists but has no UI — the arrows were dropped as clutter). Data +
// item mutations come from `useAlbum`; the album's own rename/delete call the client
// directly (they change the summary, so they re-load / navigate away).
//
// Sharing is ASYNCHRONOUS (ADR-0061). Share only *enqueues* the publish-copy, so the share row
// renders off `publishState` rather than off the Share call returning: "publishing" → an
// indeterminate "Generating link…" (no numeric progress, by decision) with no copyable link on a
// first share, plus a secondary Cancel (unshare) so a job that never lands is never a dead end;
// "failed" → "Couldn't generate the link" + Retry (re-calls share, which resumes);
// "idle" + shared → the link + Copy, as before. An already-live link KEEPS showing while a
// re-publish runs. `useAlbum` polls the album while it's publishing. The LIVE link's Unshare
// keeps its two-step confirm (killing a live link is permanent — re-sharing mints a new one).

import { useEffect, useRef, useState } from "react";
import type { ApiClient, MediaItem } from "../api";
import { useAlbum } from "../useAlbum";
import { mediaLabel } from "../download";
import { MediaCard } from "./MediaCard";
import { Loading } from "./Loading";
import { ErrorState } from "./ErrorState";
import { EmptyState } from "./EmptyState";
import { RefreshErrorBanner } from "./RefreshErrorBanner";
import { SignedOut } from "./SignedOut";

export interface AlbumDetailProps {
  client: ApiClient;
  /** The album id (parsed from /albums/:id by the App router). */
  id: string;
  /** Back to the albums list (App routes this to /albums). */
  onBack: () => void;
  /** The album was deleted — leave the (now-gone) detail view (App routes to /albums). */
  onDeleted: () => void;
  /** Injectable download flow (tests stub it), threaded to each card. */
  onDownload?: (item: MediaItem) => Promise<void>;
}

export function AlbumDetail({ client, id, onBack, onDeleted, onDownload }: AlbumDetailProps): React.JSX.Element {
  const { album, loading, error, unauthorized, busy, removeItem, reload, refresh } = useAlbum(client, id);

  // The album's own rename/delete (change the summary → re-load or navigate away).
  const [mode, setMode] = useState<"idle" | "rename" | "delete">("idle");
  const [nameDraft, setNameDraft] = useState("");
  const [headBusy, setHeadBusy] = useState(false);
  const [headError, setHeadError] = useState<string | null>(null);
  // Sharing (ADR-0059): the Share/Unshare toggle + copy-link.
  const [shareBusy, setShareBusy] = useState(false);
  const [shareErr, setShareErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // "Copied!" is a moment, not a state — reset after 2s so a second copy re-confirms.
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
    },
    [],
  );
  // Unshare is the one IRREVERSIBLE share action (re-sharing mints a fresh token, so every
  // link already sent dies) — it gets a two-step confirm like Delete does.
  const [unshareArm, setUnshareArm] = useState(false);
  // The "Link text" edit, scoped to the album it belongs to (null → show the album's own slug).
  // Derived during render (no effect) so there's no empty-field flash and switching albums resets.
  const [slugEdit, setSlugEdit] = useState<{ id: string; value: string } | null>(null);
  const [slugBusy, setSlugBusy] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);

  if (unauthorized) return <SignedOut />;

  if (loading) return <Loading label="Loading album…" />;
  if (error && !album) {
    return <ErrorState title="Couldn’t load this album" message={error} onRetry={reload} />;
  }
  if (!album) {
    // Not loading, no error, no album — treat as not found.
    return (
      <ErrorState
        title="Album not found"
        message="This album may have been deleted."
        onRetry={onBack}
        retryLabel="Back to albums"
      />
    );
  }

  const items = album.items;

  // Sharing is asynchronous (ADR-0061): `visibility` says whether the link is LIVE, `publishState`
  // says whether a background copy is running — orthogonal, so all four combinations are legal.
  // A first share is private+publishing (link withheld until whole); an edit to a shared album is
  // shared+publishing (link stays live and copyable).
  const linkLive = album.visibility === "shared" && album.shareToken != null;
  const publishing = album.publishState === "publishing";
  const publishFailed = album.publishState === "failed";

  // The canonical public link: only the token is load-bearing; the slug is the cosmetic
  // suffix the /a page redirects to (the Owner's stored slug, or the name-derived default).
  const canonical = album.slug ?? slugify(album.name);
  const shareLink = album.shareToken
    ? `${window.location.origin}/a/${album.shareToken}/${canonical}`
    : "";
  // The current "Link text" field value: the Owner's in-progress edit for THIS album, else the
  // album's own slug (stored or name-derived). Enable Save only when it would change the canonical
  // (an empty field means "clear → name-derived", which equals slugify(name)).
  const slugDraft = slugEdit?.id === album.id ? slugEdit.value : canonical;
  const draftSlug = slugDraft.trim() === "" ? slugify(album.name) : slugify(slugDraft);
  const slugChanged = draftSlug !== canonical;

  // Share — and, on a `failed` album, Retry: the same call, since the queued copy resumes from
  // whatever is already in the public bucket (ADR-0061). It returns as soon as the job is
  // ENQUEUED, so this doesn't mean the link is ready: re-read the album (quietly, so the screen
  // doesn't blink) and let `publishState` drive the UI — `useAlbum` polls from there.
  async function doShare(): Promise<void> {
    setShareBusy(true);
    setShareErr(null);
    try {
      await client.shareAlbum(id);
      await refresh(); // pick up publishState (+ the token / visibility once the copy lands)
    } catch (e) {
      setShareErr(e instanceof Error ? e.message : "Share failed.");
    } finally {
      setShareBusy(false);
    }
  }

  async function doUnshare(): Promise<void> {
    setShareBusy(true);
    setShareErr(null);
    try {
      await client.unshareAlbum(id);
      setCopied(false);
      setUnshareArm(false);
      reload();
    } catch (e) {
      setShareErr(e instanceof Error ? e.message : "Unshare failed.");
    } finally {
      setShareBusy(false);
    }
  }

  async function copyLink(): Promise<void> {
    if (!shareLink) return;
    try {
      await navigator.clipboard?.writeText(shareLink);
      setCopied(true);
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — the link is still selectable in the field */
    }
  }

  async function saveSlug(): Promise<void> {
    setSlugBusy(true);
    setShareErr(null);
    try {
      await client.setAlbumSlug(id, slugDraft.trim());
      setSlugEdit(null); // drop the local edit → show the album's (reloaded) slug
      reload(); // pick up the stored slug (or the cleared/name-derived default)
      setCopied(false);
    } catch (e) {
      setShareErr(e instanceof Error ? e.message : "Couldn't update the link text.");
    } finally {
      setSlugBusy(false);
    }
  }

  // Make an item the album's cover (the public page header + the link-unfurl OG image, #303).
  async function makeCover(key: string): Promise<void> {
    setCoverBusy(true);
    setHeadError(null);
    try {
      await client.setAlbumCover(id, key);
      reload();
    } catch (e) {
      setHeadError(e instanceof Error ? e.message : "Couldn't set the cover.");
    } finally {
      setCoverBusy(false);
    }
  }

  async function saveRename(): Promise<void> {
    if (!album) return;
    const nm = nameDraft.trim();
    if (!nm || nm === album.name) {
      setMode("idle");
      return;
    }
    setHeadBusy(true);
    setHeadError(null);
    try {
      await client.renameAlbum(id, nm);
      reload();
      setMode("idle");
    } catch (e) {
      setHeadError(e instanceof Error ? e.message : "Rename failed.");
    } finally {
      setHeadBusy(false);
    }
  }

  async function confirmDelete(): Promise<void> {
    setHeadBusy(true);
    setHeadError(null);
    try {
      await client.deleteAlbum(id);
      onDeleted();
    } catch (e) {
      setHeadError(e instanceof Error ? e.message : "Delete failed.");
      setHeadBusy(false);
    }
  }

  return (
    <section className="album-detail" data-testid="album-detail">
      <div className="album-detail__head">
        <button type="button" className="btn" onClick={onBack} data-testid="album-back">
          ← Albums
        </button>

        {mode === "rename" ? (
          <span className="album-detail__rename-wrap">
            <input
              className="album-detail__rename"
              value={nameDraft}
              maxLength={60}
              autoFocus
              aria-label={`New name for ${album.name}`}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void saveRename();
                else if (e.key === "Escape") setMode("idle");
              }}
            />
            <button type="button" className="btn" onClick={() => void saveRename()} disabled={headBusy || !nameDraft.trim()}>
              Save
            </button>
            <button type="button" className="btn" onClick={() => setMode("idle")} disabled={headBusy}>
              Cancel
            </button>
          </span>
        ) : (
          <>
            <h2 className="album-detail__title" data-testid="album-title">
              {album.name}
            </h2>
            <span className="album-detail__count" data-testid="album-item-count">
              {album.itemCount} {album.itemCount === 1 ? "item" : "items"}
            </span>
            <span
              className={`albums__badge albums__badge--${album.visibility}`}
              data-testid="album-visibility"
            >
              {album.visibility === "shared" ? "Shared" : "Private"}
            </span>
            <div className="album-detail__head-actions">
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setNameDraft(album.name);
                  setMode("rename");
                }}
                aria-label={`Rename ${album.name}`}
              >
                Rename
              </button>
              {mode === "delete" ? (
                <span className="album-detail__confirm">
                  <span className="album-detail__confirm-q" role="alert">
                    Delete album? Your media stays — only this album view is removed.
                  </span>
                  <button
                    type="button"
                    className="btn btn--danger"
                    onClick={() => void confirmDelete()}
                    disabled={headBusy}
                    aria-label={`Confirm delete ${album.name}`}
                  >
                    {headBusy ? "Deleting…" : "Yes, delete"}
                  </button>
                  <button type="button" className="btn" onClick={() => setMode("idle")} disabled={headBusy}>
                    Cancel
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  className="btn btn--danger"
                  onClick={() => setMode("delete")}
                  aria-label={`Delete ${album.name}`}
                >
                  Delete
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {(error || headError) && (
        <RefreshErrorBanner
          className="album-detail__error"
          message={headError ?? error ?? ""}
          onRetry={reload}
          testId="album-error"
        />
      )}

      <div className="album-detail__share" data-testid="album-share">
        {linkLive ? (
          <>
            <span className="album-detail__share-label">Public link — anyone with it can view</span>
            <input
              className="album-detail__share-link"
              data-testid="album-share-link"
              readOnly
              value={shareLink}
              aria-label="Public album link"
              onFocus={(e) => e.currentTarget.select()}
            />
            <button type="button" className="btn" onClick={() => void copyLink()} data-testid="album-copy-link">
              {copied ? "Copied!" : "Copy link"}
            </button>
            {/* See the page a visitor gets — the natural check after a cover/slug edit. */}
            <a
              className="btn album-detail__share-open"
              href={shareLink}
              target="_blank"
              rel="noopener"
              data-testid="album-open-link"
            >
              Open ↗
            </a>
            {unshareArm ? (
              <span className="album-detail__confirm">
                <span className="album-detail__confirm-q" role="alert">
                  Unshare? The current link stops working for everyone — sharing again creates a
                  different link.
                </span>
                <button
                  type="button"
                  className="btn btn--danger"
                  onClick={() => void doUnshare()}
                  disabled={shareBusy}
                  data-testid="album-unshare-confirm"
                >
                  {shareBusy ? "…" : "Yes, unshare"}
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => setUnshareArm(false)}
                  disabled={shareBusy}
                >
                  Cancel
                </button>
              </span>
            ) : (
              <button
                type="button"
                className="btn"
                onClick={() => setUnshareArm(true)}
                disabled={shareBusy}
                data-testid="album-unshare"
              >
                Unshare
              </button>
            )}
            {/* A live link is NEVER taken down while a re-publish runs (ADR-0061) — the link
                above stays copyable; this only says the newest items are still landing. */}
            {publishing && (
              <span className="album-detail__publish" role="status" data-testid="album-publishing">
                <span className="album-detail__publish-dot" aria-hidden="true">
                  ●
                </span>
                Updating link — new items are still copying…
              </span>
            )}
            {publishFailed && (
              <span className="album-detail__publish album-detail__publish--failed">
                <span role="alert" data-testid="album-publish-failed">
                  Couldn’t finish updating the link — some items may be missing.
                </span>
                <button
                  type="button"
                  className="btn"
                  onClick={() => void doShare()}
                  disabled={shareBusy}
                  data-testid="album-publish-retry"
                >
                  {shareBusy ? "…" : "Retry"}
                </button>
              </span>
            )}
            <div className="album-detail__share-edit">
              <label className="album-detail__share-editlabel" htmlFor="album-slug-input">Link text</label>
              <input
                id="album-slug-input"
                className="album-detail__share-slug"
                value={slugDraft}
                maxLength={60}
                onChange={(e) => setSlugEdit({ id: album.id, value: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && slugChanged && !slugBusy) void saveSlug();
                }}
                aria-label="Custom link text"
                data-testid="album-slug-input"
              />
              <button
                type="button"
                className="btn"
                onClick={() => void saveSlug()}
                disabled={!slugChanged || slugBusy}
                data-testid="album-slug-save"
              >
                {slugBusy ? "…" : "Save"}
              </button>
              {/* The server slugifies on save (lowercase, dashes) — show the normalized result
                  live so what you type visibly matches the link you'll get. */}
              {slugChanged && (
                <span className="album-detail__slug-preview" data-testid="album-slug-preview">
                  → /a/{album.shareToken}/{draftSlug}
                </span>
              )}
            </div>
          </>
        ) : publishing ? (
          // A FIRST share withholds the link until the copy is whole (ADR-0061), so there is
          // deliberately nothing copyable here yet — and no numeric progress, by decision.
          <span className="album-detail__publish" role="status" data-testid="album-publishing">
            <span className="album-detail__publish-dot" aria-hidden="true">
              ●
            </span>
            Generating link… you can leave this page — it keeps going.
            <span className="album-detail__publish-bar" aria-hidden="true" />
            {/* The escape hatch: without it a job that never lands leaves the Owner on a
                permanently polling screen with no control at all. Same call as the `failed`
                branch's Cancel — unshare clears the token and drops publishState back to idle.
                Secondary styling: finishing the publish is still the expected outcome. */}
            <button
              type="button"
              className="btn"
              onClick={() => void doUnshare()}
              disabled={shareBusy}
              data-testid="album-publish-cancel"
            >
              {shareBusy ? "…" : "Cancel"}
            </button>
          </span>
        ) : publishFailed ? (
          <span className="album-detail__publish album-detail__publish--failed">
            <span role="alert" data-testid="album-publish-failed">
              Couldn’t generate the link.
            </span>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void doShare()}
              disabled={shareBusy}
              data-testid="album-publish-retry"
            >
              {shareBusy ? "Retrying…" : "Retry"}
            </button>
            {/* A failed first share keeps its token + partial copies so Retry resumes cheaply
                (ADR-0061 §8); Unshare is the cleanup if the owner gives up instead. */}
            {album.shareToken && (
              <button
                type="button"
                className="btn"
                onClick={() => void doUnshare()}
                disabled={shareBusy}
                data-testid="album-publish-cancel"
              >
                Cancel
              </button>
            )}
          </span>
        ) : (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void doShare()}
            disabled={shareBusy}
            data-testid="album-share-btn"
          >
            {shareBusy ? "Sharing…" : "Share album"}
          </button>
        )}
        {shareErr && (
          <span role="alert" className="album-detail__share-err" data-testid="album-share-err">
            {shareErr}
          </span>
        )}
      </div>

      {items.length === 0 ? (
        <EmptyState
          icon="▤"
          title="This album is empty"
          message="Add Captures and Clips from your gallery — Select some, then “Add to album”."
        />
      ) : (
        <ul className="album-detail__grid grid" data-testid="album-grid" aria-busy={busy}>
          {items.map((item) => {
            const label = mediaLabel(item);
            return (
              <li className="grid__cell" key={item.key}>
                <MediaCard item={item} onDownload={onDownload} onReload={reload} />
                <div className="album-detail__itemctl">
                  {album.coverKey === item.key ? (
                    <span
                      className="album-detail__cover-badge"
                      data-testid="album-cover-badge"
                      aria-label={`${label} is the album cover`}
                    >
                      ★ Cover
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="btn"
                      onClick={() => void makeCover(item.key)}
                      disabled={busy || coverBusy}
                      aria-label={`Set ${label} as the album cover`}
                      data-testid="album-make-cover"
                    >
                      Cover
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn"
                    onClick={() => void removeItem(item.key)}
                    disabled={busy}
                    aria-label={`Remove ${label} from album`}
                    data-testid="album-remove-item"
                  >
                    Remove
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/**
 * The name-derived slug for the /a/<token>/<slug> suffix (only the token matters). MUST match the
 * server's `canonicalSlug` / `normalizeSlug` (lowercase, non-alphanumerics → `-`, trimmed, capped
 * 60, avoids the reserved "manifest", fallback "album") so the displayed default equals what the
 * page redirects to.
 */
function slugify(name: string): string {
  const s = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return s && s !== "manifest" ? s : "album";
}
